import jspdf from "jspdf";
import { autoTable } from "jspdf-autotable";

const getStatusColor = (status: string) => {
  const items = {
    "Fully Paid": {
      bg: "fullyPaidBg",
      text: "fullyPaidText",
    },
    Departed: {
      bg: "primaryLight",
      text: "primary",
    },
    Scheduled: {
      bg: "success50",
      text: "success100",
    },
    Cancelled: {
      text: "red500",
      bg: "red50",
    },
    Rejected: {
      text: "red500",
      bg: "red50",
    },
    Pending: {
      text: "primary",
      bg: "primaryLight",
    },
    Confirmed: {
      bg: "success50",
      text: "success100",
    },
    Approved: {
      bg: "success50",
      text: "success100",
    },
    Paid: {
      bg: "fullyPaidBg",
      text: "fullyPaidText",
    },
    Other: { text: "white", bg: "text" },
  };
  return items[status] || items["Other"];
};

const getStorageKey = (): string => {
  if (typeof window === "undefined") return "zedfo-admin-user";
  const path = window.location.pathname;
  if (path.startsWith("/store")) return "zedfo-store-user";
  return "zedfo-admin-user";
};

const getStorageKeyForRole = (role?: string): string => {
  if (role === "store") return "zedfo-store-user";
  return "zedfo-admin-user";
};

const getUser = () => {
  try {
    const key = getStorageKey();
    const raw =
      typeof window !== "undefined" ? localStorage.getItem(key) : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Basic shape check to avoid prototype pollution or unexpected objects
    if (parsed && typeof parsed === "object") {
      return parsed;
    }
    return null;
  } catch (_) {
    // Corrupt or tampered storage; clear and treat as logged out
    try {
      const key = getStorageKey();
      localStorage.removeItem(key);
    } catch (_) {}
    return null;
  }
};

const removeUser = () => {
  try {
    const key = getStorageKey();
    return localStorage.removeItem(key);
  } catch (_) {
    return null;
  }
};

const saveUser = (data: any) => {
  try {
    // Determine key from the user's role in the data, or fallback to current path
    const key = data?.role ? getStorageKeyForRole(data.role) : getStorageKey();
    // Store only expected primitives/objects to reduce risk of accidental large/PII dumps
    console.info(`[Auth] Saving user session for ID: ${data?.id || 'unknown'}`);
    return localStorage.setItem(key, JSON.stringify(data));
  } catch (_) {
    return null;
  }
};

const logout = (dispatch: any) => {
  const role = window.location.pathname.startsWith("/store") ? "store" : "admin";
  const user = getUser();
  console.info(`[Auth] Triggering logout for User ID: ${user?.id || 'unknown'} (Role: ${role})`);
  
  removeUser();
  if (dispatch) {
    const { Actions } = require("../store");
    dispatch(Actions.resetAll());
  }
  
  window.location.href = role === "store" ? "/store-login" : "/admin-login";
};

const toCamelCase = (str: string) => {
  return str
    .toLowerCase()
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
      index === 0 ? word.toLowerCase() : word.toUpperCase(),
    )
    .replace(/\s+/g, "");
};

const COLORS = [
  "#007bff",
  "#6f42c1",
  "#e83e8c",
  "#fd7e14",
  "#20c997",
  "#17a2b8",
];

const getDynamicColor = (input: string) => {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = input.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % COLORS.length;
  return COLORS[index];
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const options: any = { month: "short", day: "numeric", year: "numeric" };
  return date.toLocaleDateString("en-US", options);
};

// Format time function
const formatTime = (dateString) => {
  const date = new Date(dateString);
  return date
    .toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();
};
const downloadPDF = (head, body, title) => {
  const doc = new jspdf();
  autoTable(doc, {
    head: [head],
    body: body,
    styles: { fontSize: 4, cellPadding: 2 },
    headStyles: { fillColor: "#2770bc", fontSize: 4, fontStyle: "normal" },
    theme: "grid",
  });
  doc.save(title);
};
// utils/variant.ts
export const generateCombinations = (variants: Record<string, any[]>) => {
  const entries = Object.entries(variants).filter(([_, arr]) => arr.length > 0);

  if (entries.length === 0) return [];

  const combine = (arr: any[][]) => {
    return arr.reduce((a, b) => a.flatMap((d) => b.map((e) => [...d, e])), [
      [],
    ] as any[][]);
  };

  const valuesArray = entries.map(([_, values]) =>
    values.map((v) => (typeof v === "object" ? v.name || v.title || v : v)),
  );

  const combinations = combine(valuesArray);

  return combinations.map((combo) => combo.join(" "));
};
const getInitials = (name: string): string => {
  if (!name) return "";

  return name
    .trim()
    .split(/\s+/) // split by one or more spaces
    .map((word) => word[0]?.toUpperCase())
    .join("");
};
const generateSku = (name: string) => {
  const prefix = name.replace(/\s+/g, "-").slice(0, 8).toUpperCase();
  const random = Math.floor(1000 + Math.random() * 9000);
  return `SKU-${prefix}-${random}`;
};
function toCapitalize(str) {
  if (!str) return ""; // handle empty or undefined
  return str.charAt(0).toUpperCase() + str.slice(1);
}
export const formatCurrency = (price: number | string, settings: any) => {
  if (!settings?.currencySymbol) return price;
  const space = " "; // add one space for readability
  return settings.currencyPosition === "left"
    ? `${settings.currencySymbol}${space}${price}`
    : `${price}${space}${settings.currencySymbol}`;
};
export const formatDateTime = (
  date: string | Date,
  settings: { timeZone?: string; timeFormat?: string },
) => {
  if (!date) return "";

  const options: Intl.DateTimeFormatOptions = {
    // Server timestamps carry Nepal wall-clock digits labelled as UTC, so format
    // in UTC to render them verbatim (see src/utils/timezone.ts).
    timeZone: settings.timeZone || "UTC",
    hour12: settings.timeFormat === "12",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  };

  return new Intl.DateTimeFormat("en-US", options).format(new Date(date));
};

const toLowerCaseNoSpaces = (str: string) => {
  return str.toLowerCase().replace(/\s+/g, "");
};

const formatDuration = (seconds: number) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  let result = "";
  if (hrs > 0) result += `${hrs}h `;
  result += `${mins}m ${secs}s`;

  return result.trim();
};
const getDeviceId = () => {
  if (typeof window === "undefined") return "";
  let deviceId = localStorage.getItem("zedfo-device-id");
  if (!deviceId) {
    deviceId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2);
    localStorage.setItem("zedfo-device-id", deviceId);
  }
  return deviceId;
};

export default {
  getStatusColor,
  getUser,
  removeUser,
  saveUser,
  logout,
  toCamelCase,
  getDynamicColor,
  formatDate,
  formatTime,
  downloadPDF,
  generateCombinations,
  getInitials,
  generateSku,
  toCapitalize,
  formatCurrency,
  formatDateTime,
  toLowerCaseNoSpaces,
  formatDuration,
  getDeviceId,
};
